'use client';

/**
 * LES 28 LIVRES DU SAINT CORAN — bandeau royal affiché avant le footer.
 * 28 éditions prestige (photos réelles KOPAH) + le Coffret Royal en carte
 * vedette, avec lightbox. Le bandeau utilise la bannière dorée générée.
 */
import { useState } from 'react';
import Image from 'next/image';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import products from '@/data/products.json';
import { Crown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type P = { id: string; cat: string; brand: string; label: string };

const CORAN = (products as P[]).filter((p) => p.cat === 'coran');
const COFFRET_ID = 'coran/CORAN_03_EN_coffret_royal.jpg';
const COFFRET = CORAN.find((p) => p.id === COFFRET_ID);
const BOOKS = CORAN.filter((p) => p.id !== COFFRET_ID); // 28 livres

export default function BooksStrip() {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];
  const [zoom, setZoom] = useState<{ src: string; label: string; brand: string } | null>(null);

  return (
    <section aria-label={t.books.title} className="relative mt-8 border-t border-[rgba(212,175,55,0.25)] py-14">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Bannière dorée (remplace l'ancien en-tête texte) */}
        <div className="relative mb-6 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.35)] shadow-[0_0_30px_rgba(212,175,55,0.15)]">
          <Image
            src="/images/banners/books_banner.png"
            alt=""
            width={1440}
            height={704}
            unoptimized
            className="h-[180px] w-full object-cover sm:h-[240px]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,25,19,0.45),rgba(3,32,25,0.9))]" aria-hidden />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.34em] text-[#d4af37]/90">{t.books.eyebrow}</p>
            <h2 className="font-serif-royal text-3xl font-bold sm:text-4xl">
              <span className="text-gold-gradient">{t.books.title}</span>
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#cfe0cf]/85">{t.books.sub}</p>
          </div>
        </div>

        <div className="ornament-divider mb-6">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)]">
            <Crown className="h-4 w-4 text-[#d4af37]" aria-hidden />
          </span>
        </div>

        {/* Coffret royal en vedette */}
        {COFFRET && (
          <button
            onClick={() => setZoom({ src: `/images/products/${COFFRET.id}`, label: t.books.coffret_label, brand: COFFRET.brand })}
            className="group relative mx-auto mb-8 flex w-full max-w-3xl flex-col items-center overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.45)] shadow-[0_0_40px_rgba(212,175,55,0.18)] transition-all hover:border-[rgba(212,175,55,0.8)] hover:shadow-[0_0_60px_rgba(212,175,55,0.3)] sm:flex-row"
          >
            <span className="relative h-56 w-full shrink-0 overflow-hidden sm:h-44 sm:w-56">
              <Image
                src={`/images/products/${COFFRET.id}`}
                alt={t.books.coffret_label}
                fill
                unoptimized
                sizes="(max-width: 640px) 100vw, 224px"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </span>
            <span className="flex flex-1 flex-col items-center gap-1.5 p-5 text-center sm:items-start sm:text-left">
              <span className="rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.12)] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#f0d98c]">
                {t.books.coffret_note}
              </span>
              <span className="font-serif-royal text-xl font-bold text-[#f0d98c]">{t.books.coffret_label}</span>
              <span className="text-xs uppercase tracking-widest text-[#cfe0cf]/60">{COFFRET.brand}</span>
            </span>
          </button>
        )}

        {/* Les 28 livres */}
        <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7">
          {BOOKS.map((p) => (
            <button
              key={p.id}
              onClick={() => setZoom({ src: `/images/products/${p.id}`, label: p.label, brand: p.brand })}
              className="group relative aspect-square overflow-hidden rounded-xl border border-[rgba(212,175,55,0.25)] transition-all hover:border-[rgba(212,175,55,0.75)] hover:shadow-[0_0_24px_rgba(212,175,55,0.3)]"
              aria-label={p.label}
            >
              <Image
                src={`/images/products/${p.id}`}
                alt={p.label}
                fill
                unoptimized
                sizes="(max-width: 640px) 33vw, (max-width: 1024px) 25vw, 14vw"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[rgba(2,25,19,0.92)] to-transparent px-1.5 pb-1 pt-5 opacity-0 transition-opacity group-hover:opacity-100">
                <span className="block truncate text-[9px] font-bold text-[#f0d98c]">{p.label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(1,15,11,0.94)] p-4 backdrop-blur-sm"
          onClick={() => setZoom(null)}
          role="dialog"
          aria-modal="true"
          aria-label={zoom.label}
        >
          <div className="relative max-h-[88vh] w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <Image
              src={zoom.src}
              alt={zoom.label}
              width={1024}
              height={1024}
              unoptimized
              className={cn('mx-auto max-h-[78vh] w-auto rounded-xl border border-[rgba(212,175,55,0.5)] object-contain shadow-[0_0_60px_rgba(212,175,55,0.25)]')}
            />
            <div className="mt-3 text-center">
              <p className="font-serif-royal text-lg font-bold text-[#f0d98c]">{zoom.label}</p>
              <p className="text-xs uppercase tracking-widest text-[#cfe0cf]/60">{zoom.brand}</p>
            </div>
            <button
              onClick={() => setZoom(null)}
              className="absolute -top-2 right-0 flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(3,32,25,0.9)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.2)]"
              aria-label={t.common.close}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
